import React, { useEffect, useRef, useState } from 'react';
import UploadIcon from '../assets/upload_icon.png';
import Cropper from 'react-easy-crop';
import Label from './common/Label';
import Button from './common/Button';
import { CropCoordinates, CroppedAreaCoordinates } from '../types/types';
import Text from './common/Text';
import arrowRotateRight from '../assets/arrow-rotate-right.png';
import 'rc-slider/assets/index.css';
import Slider from 'rc-slider';
interface FileUploadProps {
  onUploadComplete: (blob: Blob, file: File) => void;
}

const ImageCropper: React.FC<FileUploadProps> = ({ onUploadComplete }) => {
  const [file, setFile] = useState<File | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [cropData, setCropData] = useState<CropCoordinates>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState<number>(1.5);
  const [rotation, setRotation] = useState<number>(0);
  const [showCropModal, setShowCropModal] = useState<boolean>(false);
  const [croppedBlob, setCroppedBlob] = useState<Blob | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml'];
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  useEffect(() => {
    if (file) {
      const blobImageUrl = URL.createObjectURL(file);
      setImageUrl(blobImageUrl);
      return () => {
        URL.revokeObjectURL(blobImageUrl);
      };
    }
  }, [file]);

  const createImage = (url: string): Promise<HTMLImageElement> => {
    return new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', (error) => reject(error));
      image.setAttribute('crossOrigin', 'anonymous'); // to avoid cross-origin issues
      image.src = url;
    });
  };

  const getRadianAngle = (degreeValue: number): number => {
    return (degreeValue * Math.PI) / 180;
  };

  const rotateSize = (
    width: number,
    height: number,
    rotation: number,
  ): { width: number; height: number } => {
    const rotRad = getRadianAngle(rotation);
    return {
      width:
        Math.abs(Math.cos(rotRad) * width) +
        Math.abs(Math.sin(rotRad) * height),
      height:
        Math.abs(Math.sin(rotRad) * width) +
        Math.abs(Math.cos(rotRad) * height),
    };
  };

  const getCroppedImg = async (
    imageSrc: string,
    pixelCrop: CroppedAreaCoordinates,
    rotation: number = 0,
    flip: { horizontal?: boolean; vertical?: boolean } = {
      horizontal: false,
      vertical: false,
    },
  ): Promise<Blob | null> => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) return null;

    const { width: bBoxWidth, height: bBoxHeight } = rotateSize(
      image.width,
      image.height,
      rotation,
    );
    canvas.width = bBoxWidth;
    canvas.height = bBoxHeight;

    ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
    ctx.rotate(getRadianAngle(rotation));
    ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
    ctx.translate(-image.width / 2, -image.height / 2);
    ctx.drawImage(image, 0, 0);

    const croppedCanvas = document.createElement('canvas');
    const croppedCtx = croppedCanvas.getContext('2d');

    if (!croppedCtx) return null;

    croppedCanvas.width = pixelCrop.width;
    croppedCanvas.height = pixelCrop.height;

    croppedCtx.drawImage(
      canvas,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height,
    );

    return new Promise<Blob | null>((resolve) => {
      croppedCanvas.toBlob((blob) => {
        resolve(blob || null);
      }, 'image/jpeg');
    });
  };

  const handleCropComplete = async (
    croppedArea: CroppedAreaCoordinates,
    croppedAreaPixels: CroppedAreaCoordinates,
  ) => {
    if (!file) return;

    const croppedImageBlob = await getCroppedImg(
      URL.createObjectURL(file),
      croppedAreaPixels,
      rotation,
    );

    if (croppedImageBlob) {
      setCroppedBlob(croppedImageBlob);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0] || null;

    if (selectedFile) {
      // Check if the file is an image and its size is below 500KB (500 * 1024 bytes)
      if (!validTypes.includes(selectedFile.type)) {
        setMessage('Please upload a JPEG, PNG, GIF, or SVG image.');
        return;
      }
      // will be used
      // if (selectedFile.size > 1024 * 1024) {
      //   // 1MB size limit
      //   setMessage('The file size should not exceed 1MB.');
      //   return;
      // }
      setFile(selectedFile);
      setShowCropModal(true);
      setMessage(null);
      setZoom(1.5);
      setRotation(0);
    }
  };

  const handleClick = () => {
    if (file) return;
    document.querySelector<HTMLInputElement>('input[type="file"]')?.click();
  };

  const handleSave = async () => {
    setShowCropModal(false);

    if (!croppedBlob) {
      setMessage('Please select a file to upload.');
      return;
    }

    try {
      if (file !== null) {
        onUploadComplete(croppedBlob, file);
      }
      setFile(null);
      setCroppedBlob(null);
      setZoom(1.5);
      setRotation(0);

      if (fileInputRef.current) {
        fileInputRef.current.value = ''; // This resets the file input
      }
    } catch (error) {
      setMessage('File upload failed.');
    }
  };

  const handleSliderChange = (value: number | number[]) => {
    if (typeof value === 'number') {
      setZoom(value);
    }
  };

  useEffect(() => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ''; // Reset file input to allow selecting the same file
    }
  }, [showCropModal]);

  useEffect(() => {
    if (showCropModal) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showCropModal]);

  return (
    <div className="file-upload-container">
      <Button
        type="button"
        className="file-upload-button-container"
        onClick={handleClick}
      >
        <div className="label-small">
          <div className="upload-text-container">
            <img src={UploadIcon} alt="icon" className="upload-icon" />
            <div>
              <Text className="small-regular">
                <Text className="upload-text">Click to upload</Text>{' '}
                <Text>or drag and drop</Text>
              </Text>
            </div>
            <Text className="extra-small">
              SVG, PNG, JPG or GIF (max. 800x400px)
            </Text>
          </div>
        </div>

        <input
          type="file"
          onChange={handleFileChange}
          ref={fileInputRef}
          style={{ display: 'none' }}
          aria-label="File upload input"
        />

        {message && <Text className="error-message">{message}</Text>}
      </Button>
      {showCropModal && file && (
        <div className="popup-overlay" role="presentation">
          <div
            ref={modalRef}
            className={`crop-modal-container ${showCropModal && file ? 'active' : ''}`}
            role="dialog"
            aria-modal="true"
          >
            <div className="grid gap-8">
              <Text className="large-semibold">Crop Profile Photo</Text>
              <div className="crop-area">
                <Cropper
                  image={imageUrl || ''}
                  crop={cropData}
                  zoom={zoom}
                  rotation={rotation}
                  aspect={4 / 3}
                  onCropChange={setCropData}
                  onZoomChange={setZoom}
                  onRotationChange={setRotation}
                  onCropComplete={handleCropComplete}
                  showGrid={false}
                />
              </div>
            </div>

            <div className="crop-controllers">
              <div className="control">
                <Label className="input-label">
                  Zoom: {Math.round(((zoom - 1) / 2) * 100)}%
                </Label>
                <Button
                  type="button"
                  className="rotate-button"
                  onClick={() =>
                    setRotation((prevRotation) => (prevRotation + 90) % 360)
                  }
                >
                  <img
                    src={arrowRotateRight}
                    alt="Rotate"
                    className="rotate-icon"
                  />
                </Button>
              </div>

              <div className="control">
                <Slider
                  min={1}
                  max={3}
                  step={0.01}
                  value={zoom}
                  onChange={handleSliderChange}
                  className="slider"
                />
              </div>
            </div>

            <div className="nav-bar">
              <Button
                type="button"
                className="secondary-button-medium"
                onClick={() => {
                  setFile(null);
                  setShowCropModal(false);
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                className="primary-button-medium"
                onClick={handleSave}
              >
                Crop and Save
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageCropper;
